import { Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

export interface TextFilterProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  label?: string;
  debounceMs?: number;
  className?: string;
  /**
   * Compact mode for inset filter bars inside a DataTable card: drops the
   * fixed 280px width so the input can flex, and hides the uppercase label
   * (placeholder carries the meaning).
   */
  compact?: boolean;
}

export function TextFilter({
  value,
  onChange,
  placeholder = 'Search',
  label,
  debounceMs = 300,
  className,
  compact = false,
}: TextFilterProps) {
  const [local, setLocal] = useState(value);
  const lastEmitted = useRef(value);

  // Controlled-value sync: when the parent's value prop changes (e.g. clear-all
  // fires), reset local state and lastEmitted so we don't re-emit a stale value.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLocal(value);
    lastEmitted.current = value;
  }, [value]);

  useEffect(() => {
    if (local === lastEmitted.current) return;
    const id = setTimeout(() => {
      lastEmitted.current = local;
      onChange(local);
    }, debounceMs);
    return () => {
      clearTimeout(id);
    };
  }, [local, debounceMs, onChange]);

  const showLabel = label && !compact;

  return (
    <div className={cn('flex flex-col gap-1', compact && 'flex-1', className)}>
      {showLabel ? (
        <span className="text-meta-sm tracking-label font-medium text-neutral-500 uppercase">
          {label}
        </span>
      ) : null}
      <div className={cn('relative', compact ? 'w-full max-w-md' : 'w-70')}>
        <Search
          className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400"
          strokeWidth={1.5}
          aria-hidden="true"
        />
        <input
          type="text"
          value={local}
          onChange={(e) => {
            setLocal(e.target.value);
          }}
          placeholder={placeholder}
          aria-label={label}
          className={cn(
            'bg-surface h-10 w-full rounded-md border border-(--color-input) pr-9 pl-9',
            'text-body-md text-neutral-900 placeholder:text-neutral-500',
            'transition-[border,box-shadow]',
            'focus-visible:border-primary-300 focus-visible:shadow-(--shadow-focus-ring) focus-visible:outline-none',
          )}
        />
        {local ? (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              setLocal('');
              lastEmitted.current = '';
              onChange('');
            }}
            className={cn(
              'absolute top-1/2 right-2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-sm text-neutral-400',
              'hover:bg-neutral-100 hover:text-neutral-600',
              'focus-visible:shadow-(--shadow-focus-ring-solid) focus-visible:outline-none',
            )}
          >
            <X className="h-3 w-3" strokeWidth={1.5} />
          </button>
        ) : null}
      </div>
    </div>
  );
}
